/**
 * DeviceHoverCard (DE-161) — popover content rendered on hover over a
 * device row in OpsDeviceGroups. Reads everything it needs from the
 * existing DeviceSummary shape so no new backend call is involved; the
 * parent (OpsDeviceGroups) joins useDevices() against the list of thing
 * names from useDeviceGroups() and passes the looked-up row in.
 *
 * Layout matches the Leaflet popup body in DeviceMapMarker.tsx so the
 * portal stays visually consistent between the map and the groups page —
 * same font sizes, same label-column width, same pill/status logic via
 * deviceStatus() shared with the map.
 *
 * `device === undefined` handles the cross-ref miss for a Thing that
 * exists in the IoT registry but hasn't yet published anything to RDS
 * (newly-provisioned device sitting in artomeui-unassigned). Mirrors
 * the DeviceNotYetPublishing placeholder copy in DeviceDetail.tsx.
 */
import { effectiveFix, type DeviceSummary } from '../api/hooks';
import { deviceStatus } from './DeviceMapMarker';
import { addressWithoutVenue, fmtAgo, humanisePlaceType } from './format';
import { locationConfidence } from '../lib/locationConfidence';

interface DeviceHoverCardProps {
  /** Thing name being hovered. Always available even when device row is missing. */
  thing: string;
  /** Looked-up DeviceSummary; undefined when the device hasn't published yet. */
  device: DeviceSummary | undefined;
}

export function DeviceHoverCard({ thing, device }: DeviceHoverCardProps) {
  if (!device) {
    return (
      <div className="bg-white border border-border rounded-md shadow-lg p-3 text-xs space-y-1.5 w-72">
        <div className="font-mono text-fg break-all">{thing}</div>
        <p className="text-muted">No telemetry yet — device hasn't published.</p>
      </div>
    );
  }

  const status = deviceStatus(device);
  const statusPillClass =
    status === 'online' ? 'pill-accent' :
    status === 'stale' ? 'pill-warn' :
    'pill-muted';
  const statusLabel = status === 'stale' ? 'stale' : status;
  const fix = effectiveFix(device.geo);
  // DE-281 — the hover card showed fmtAgo(fix.timestamp), i.e. how long ago the
  // position was ESTABLISHED. On the fleet map that reads as "this pin is N days
  // stale" for devices being re-confirmed every few minutes.
  const confidence = locationConfidence({
    geo: device.geo, online: device.online, lastSeen: device.last_seen });

  return (
    <div className="bg-white border border-border rounded-md shadow-lg p-3 text-xs space-y-1.5 w-72">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium text-fg">
          {device.friendly_name ?? (
            <span className="font-mono break-all">{device.thing}</span>
          )}
        </span>
        <span className={statusPillClass}>{statusLabel}</span>
      </div>
      {device.friendly_name && (
        // Surface the thing-name underneath the friendly name so the
        // operator can still see the canonical identifier without
        // clicking through.
        <div className="font-mono text-muted text-[10px] break-all">
          {device.thing}
        </div>
      )}
      <div className="flex items-baseline gap-2 py-0.5">
        <span className="text-muted w-16 shrink-0">fw</span>
        <span className="font-mono break-all">{device.fw_ver ?? '—'}</span>
      </div>
      <div className="flex items-baseline gap-2 py-0.5">
        <span className="text-muted w-16 shrink-0">mender</span>
        <span className="font-mono break-all">{device.mender_artifact ?? '—'}</span>
      </div>
      <div className="flex items-baseline gap-2 py-0.5">
        <span className="text-muted w-16 shrink-0">last seen</span>
        <span className="tabular-nums">{fmtAgo(device.last_seen)}</span>
      </div>
      <div className="flex items-baseline gap-2 py-0.5">
        <span className="text-muted w-16 shrink-0">location</span>
        {fix ? (
          // DE-170: lead with the words when we have them — "which building"
          // is the question an operator is actually asking — and demote the
          // coords to the muted second line. Falls back to the original
          // coords-first layout when enrichment is absent (old fix, coarse
          // fix, or Geoapify unconfigured).
          // !verified means we fell back to last_verified_* — flagged so the
          // operator knows the position predates the current shadow fix.
          <span>
            {(fix.place_name || fix.place_type || fix.address) && (
              <span className="block text-fg">
                {fix.place_name ?? humanisePlaceType(fix.place_type) ?? fix.address}
              </span>
            )}
            {/* Second line only when it adds something the heading didn't. */}
            {(fix.place_name || fix.place_type) &&
              addressWithoutVenue(fix.address, fix.place_name) && (
                <span className="block text-muted text-[10px]">
                  {addressWithoutVenue(fix.address, fix.place_name)}
                </span>
              )}
            <span
              className={
                'block tabular-nums' +
                (fix.place_name || fix.place_type || fix.address
                  ? ' text-muted text-[10px]'
                  : '')
              }
            >
              {fix.lat.toFixed(5)}, {fix.lon.toFixed(5)}
              {fix.accuracy ? ` ±${Math.round(fix.accuracy)} m` : ''}
              {fix.source && <span className="text-muted"> · {fix.source}</span>}
            </span>
            <span
              className={
                'block text-[10px] ' +
                (confidence.tone === 'warn' ? 'text-warn' : 'text-muted')
              }
              title={confidence.explanation}
            >
              {confidence.headline}
            </span>
          </span>
        ) : (
          <span className="text-muted">no location yet</span>
        )}
      </div>
    </div>
  );
}
